import { type AttentionItem, type Portfolio, PROJECT_STAGES, type ProjectSummary } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { WeeklyBars } from '../../components/charts';
import { date, percent, plural, tengeShort, todayLocal } from '../../lib/format';
import { mln, portfolioQuery, projectTitle, SEVERITY_TONE, STAGE_TONE } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ProjectWizard } from '../projects/ProjectWizard';

function stateOf(p: ProjectSummary, attention: AttentionItem[]) {
  const own = attention.filter((a) => a.projectId === p.id);
  if (own.some((a) => a.severity === 'critical')) return { tone: 'danger' as const, text: 'Есть блокеры' };
  if (own.some((a) => a.severity === 'warning')) return { tone: 'warning' as const, text: 'Есть вопросы' };
  if (p.metrics.scheduleDeviationDays && p.metrics.scheduleDeviationDays > 0)
    return { tone: 'warning' as const, text: `+${p.metrics.scheduleDeviationDays} дн` };
  return { tone: 'success' as const, text: 'В графике' };
}

function Progress({ value }: { value: number | null }) {
  if (value === null) return <span className="text-ink-3">—</span>;
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="flex min-w-24 flex-col gap-1">
      <div className="h-1.5 rounded-full bg-tile">
        <div className="h-1.5 rounded-full bg-ink" style={{ width: `${v}%` }} />
      </div>
      <span className="num text-[12px] text-ink-2">{percent(value, 0)}</span>
    </div>
  );
}

function Kpis({ data }: { data: Portfolio }) {
  const k = data.kpi;
  const critical = data.attention.filter((a) => a.severity === 'critical').length;
  // Суммы видны только в части проектов (ДС-18): итог по всем проектам не показывается, чтобы не вводить в заблуждение
  const partial = Boolean(data.moneyPartial);
  const hidden = data.moneyHidden || partial;
  const why = partial ? 'итог скрыт: суммы видны не во всех проектах' : 'суммы открывает владелец компании';
  return (
    <TitleBlock
      className="mb-6"
      items={[
        {
          label: 'Бюджет проектов в работе',
          value: hidden ? 'скрыто' : tengeShort(k.budgetTotal),
          note: hidden
            ? why
            : k.estimateValue
              ? `сметная стоимость ${tengeShort(k.estimateValue)}`
              : 'сметная стоимость — со сметами (этап 4)',
        },
        {
          label: 'Маржа, прогноз',
          value: (
            <span className={clsx(k.forecastMarginPct !== null && 'visor inline-block px-1.5')}>
              {percent(k.forecastMarginPct ?? k.planMarginPct)}
            </span>
          ),
          note:
            k.planMarginPct !== null ? `план ${percent(k.planMarginPct)}` : 'считается от сметы и бюджета',
        },
        {
          label: 'Оплачено с начала',
          value: hidden ? 'скрыто' : tengeShort(k.paidToDate),
          note: hidden ? (partial ? why : undefined) : `план на дату ${tengeShort(k.planToDate)}`,
        },
        {
          label: 'Сроки',
          value:
            k.scheduleDeviationDays && k.scheduleDeviationDays > 0
              ? `+${k.scheduleDeviationDays} дн`
              : 'в графике',
          tone: k.scheduleDeviationDays && k.scheduleDeviationDays > 0 ? 'accent' : 'default',
          note: k.scheduleNote ?? 'по вехам проектов',
        },
        {
          label: 'Требует внимания',
          value: k.openAttention,
          tone: critical ? 'danger' : 'default',
          note: critical
            ? `${plural(critical, 'блокер', 'блокера', 'блокеров')} утверждения`
            : 'находки модулей',
        },
      ]}
    />
  );
}

function ProjectsTable({ data }: { data: Portfolio }) {
  const tops = data.projects.filter((p) => !p.parentId || !data.projects.some((x) => x.id === p.parentId));
  const subsOf = (id: string) => data.projects.filter((p) => p.parentId === id);
  const row = (p: ProjectSummary, sub: boolean) => {
    const st = stateOf(p, data.attention);
    const objects = p.objects.filter((o) => !o.parentId);
    return (
      <tr key={p.id} className="border-t border-line align-top hover:bg-surface-2">
        <td className={clsx('py-3 pr-4', sub ? 'pl-8' : 'pl-5')}>
          <Link to={`/projects/${p.id}`} className="font-semibold text-ink hover:underline">
            {sub ? p.name : projectTitle(p)}
          </Link>
          <div className="text-[13px] text-ink-2">
            {[objects.map((o) => o.code ?? o.name).join(', '), p.code].filter(Boolean).join(' · ')}
          </div>
        </td>
        <td className="py-3 pr-4">
          <Badge tone={STAGE_TONE[p.stage]}>{PROJECT_STAGES[p.stage]}</Badge>
        </td>
        <td className="num py-3 pr-4 text-right">{mln(p.metrics.budgetTotal)}</td>
        <td className="num py-3 pr-4 text-right">
          {percent(p.metrics.planMarginPct)} / {percent(p.metrics.forecastMarginPct)}
        </td>
        <td className="py-3 pr-4">
          <Progress value={p.metrics.paidPctOfBudget} />
        </td>
        <td className="num py-3 pr-4 whitespace-nowrap">{date(p.finishDate)}</td>
        <td className="py-3 pr-5">
          <Badge tone={st.tone}>{st.text}</Badge>
        </td>
      </tr>
    );
  };
  return (
    <ScrollX>
      <table className="w-full min-w-[860px] text-[14px]">
        <thead>
          <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
            <th className="py-2.5 pr-4 pl-5 font-medium">Проект</th>
            <th className="py-2.5 pr-4 font-medium">Стадия</th>
            <th className="py-2.5 pr-4 text-right font-medium">Бюджет, млн ₸</th>
            <th className="py-2.5 pr-4 text-right font-medium">Маржа план / прогноз</th>
            <th className="py-2.5 pr-4 font-medium">Оплачено от бюджета</th>
            <th className="py-2.5 pr-4 font-medium">Срок</th>
            <th className="py-2.5 pr-5 font-medium">Состояние</th>
          </tr>
        </thead>
        <tbody>{tops.flatMap((p) => [row(p, false), ...subsOf(p.id).map((s) => row(s, true))])}</tbody>
      </table>
    </ScrollX>
  );
}

export function AttentionList({ items, limit }: { items: AttentionItem[]; limit?: number }) {
  const list = limit ? items.slice(0, limit) : items;
  if (!list.length) return <p className="text-[14px] text-ink-2">Всё в порядке: находок нет.</p>;
  return (
    <ul className="divide-y divide-line">
      {list.map((a) => (
        <li key={a.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
          <span
            aria-hidden
            className={clsx(
              'mt-1.5 size-2.5 shrink-0 rounded-[2px]',
              a.severity === 'critical' ? 'bg-danger' : a.severity === 'warning' ? 'bg-warning' : 'bg-ink-3',
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] leading-5 font-medium text-ink">{a.title}</p>
            <p className="mt-0.5 line-clamp-2 text-[13px] text-ink-2">
              {[a.projectCode, a.details].filter(Boolean).join(' · ')}
            </p>
          </div>
          {a.actionPath ? (
            <Link
              to={a.actionPath}
              className="inline-flex h-11 shrink-0 items-center rounded-md border border-line-strong px-3 text-[13px] font-medium text-ink hover:bg-surface-2 md:h-8"
            >
              {a.actionLabel ?? 'Открыть'}
            </Link>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function StructureTable({ data }: { data: Portfolio }) {
  const pad = { company: 'pl-5', direction: 'pl-9', project: 'pl-13', subproject: 'pl-17' } as const;
  return (
    <ScrollX>
      <table className="w-full min-w-[900px] text-[14px]">
        <thead>
          <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
            <th className="py-2.5 pr-4 pl-5 font-medium">Компания, направление, проект</th>
            <th className="py-2.5 pr-4 font-medium">Проектов</th>
            <th className="py-2.5 pr-4 text-right font-medium">Бюджет, млн ₸</th>
            <th className="py-2.5 pr-4 text-right font-medium">Разложено по неделям</th>
            <th className="py-2.5 pr-4 text-right font-medium">Маржа план / прогноз</th>
            <th className="py-2.5 pr-4 text-right font-medium">Оплачено</th>
            <th className="py-2.5 pr-4 text-right font-medium">Остаток к оплате</th>
            <th className="py-2.5 pr-5 font-medium">Внимание</th>
          </tr>
        </thead>
        <tbody>
          {data.structure.map((r) => {
            const strong = r.level === 'company' || r.level === 'direction';
            const left =
              r.metrics.budgetTotal !== null ? r.metrics.budgetTotal - (r.metrics.paidToDate ?? 0) : null;
            return (
              <tr
                key={`${r.level}:${r.id}`}
                className={clsx('border-t border-line', r.level === 'company' && 'bg-surface-2/60')}
              >
                <td className={clsx('py-2.5 pr-4', pad[r.level])}>
                  {r.level === 'project' || r.level === 'subproject' ? (
                    <Link to={`/projects/${r.id}`} className="text-ink hover:underline">
                      {r.name}
                    </Link>
                  ) : (
                    <span className={clsx(strong && 'font-semibold', 'text-ink')}>{r.name}</span>
                  )}
                  {r.level === 'direction' ? (
                    <span className="ml-2 text-[12px] text-ink-3">направление</span>
                  ) : null}
                  {r.level === 'subproject' ? (
                    <span className="ml-2 text-[12px] text-ink-3">подпроект</span>
                  ) : null}
                </td>
                <td className="num py-2.5 pr-4 text-ink-2">
                  {r.level === 'subproject'
                    ? ''
                    : r.subprojects
                      ? `${r.projects} · ${plural(r.subprojects, 'подпроект', 'подпроекта', 'подпроектов')}`
                      : r.projects}
                </td>
                <td className="num py-2.5 pr-4 text-right">{mln(r.metrics.budgetTotal)}</td>
                <td className="num py-2.5 pr-4 text-right">{mln(r.metrics.budgetDistributed)}</td>
                <td className="num py-2.5 pr-4 text-right">
                  {percent(r.metrics.planMarginPct)} / {percent(r.metrics.forecastMarginPct)}
                </td>
                <td className="num py-2.5 pr-4 text-right">{mln(r.metrics.paidToDate)}</td>
                <td className="num py-2.5 pr-4 text-right">{mln(left)}</td>
                <td className="py-2.5 pr-5">
                  {r.worstSeverity ? (
                    <Badge tone={SEVERITY_TONE[r.worstSeverity]}>{r.attention}</Badge>
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollX>
  );
}

export function PortfolioPage() {
  const me = useMe();
  const [le, setLe] = useState<string>('');
  const [view, setView] = useState<'projects' | 'structure'>('projects');
  const [wizard, setWizard] = useState(false);
  const canCreate = useCan('project.create');
  const q = useQuery(portfolioQuery(le || undefined));

  const weeks = useMemo(() => {
    const t = todayLocal();
    const next = (q.data?.cashflow ?? []).filter(
      (p) => p.start >= t || Date.parse(p.start) + 7 * 86_400_000 > Date.parse(t),
    );
    return next.slice(0, 6).map((p) => ({
      label: p.label.replace(/^[^,]+, /, '').replace(' нед.', ''),
      value: p.plan,
      month: p.label.split(',')[0],
    }));
  }, [q.data]);
  const weeksTotal = weeks.reduce((s, w) => s + w.value, 0);

  const subtitle = [
    `на ${date(new Date())}`,
    le ? me.legalEntities.find((x) => x.id === le)?.name : me.tenant?.name,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Page>
      <PageHeader
        title="Портфель проектов"
        subtitle={subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {me.legalEntities.length > 1 ? (
              <Select
                aria-label="Юрлицо"
                value={le}
                onChange={(e) => setLe(e.target.value)}
                className="h-10 w-56"
              >
                <option value="">Все юрлица группы</option>
                {me.legalEntities.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.shortName}
                  </option>
                ))}
              </Select>
            ) : null}
            {me.tenant?.staff ? (
              // ДК-11: сообщить об опасности может любой сотрудник, в один шаг с главной
              <Link
                to="/qhse/observe"
                search={{ project: undefined }}
                className="inline-flex h-11 items-center gap-2 rounded-md border border-line bg-surface px-4 text-[14px] font-semibold text-ink hover:underline md:h-10"
              >
                <TriangleAlert className="size-4 text-warning" aria-hidden />
                Сообщить об опасности
              </Link>
            ) : null}
            {canCreate ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setWizard(true)}>
                Новый проект
              </Button>
            ) : null}
          </div>
        }
      />

      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.projects.length ? (
        <EmptyState
          title="Проектов пока нет"
          action={
            canCreate ? (
              <Button variant="primary" onClick={() => setWizard(true)}>
                Создать проект
              </Button>
            ) : null
          }
        >
          Создайте проект мастером: компания, направление, заказчик и договор, структура объектов.
        </EmptyState>
      ) : (
        <>
          <Kpis data={q.data} />
          <div className="mb-4 inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist">
            {(
              [
                ['projects', 'Проекты'],
                ['structure', 'Своды по направлениям'],
              ] as const
            ).map(([k, t]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={view === k}
                onClick={() => setView(k)}
                className={clsx(
                  'h-11 rounded-[8px] px-3 text-[13px] font-medium md:h-8',
                  view === k ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {t}
              </button>
            ))}
          </div>
          <Panel
            className="mb-6"
            title={
              view === 'projects' ? 'Проекты и объекты' : 'Свод: компания → направление → проект → подпроект'
            }
            meta={q.data.moneyHidden ? 'суммы скрыты' : undefined}
            bodyClassName="p-0"
          >
            {view === 'projects' ? <ProjectsTable data={q.data} /> : <StructureTable data={q.data} />}
          </Panel>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Panel title="Требует внимания" meta={q.data.attention.length}>
              <AttentionList items={q.data.attention} />
            </Panel>
            <Panel
              title="Оплаты по плану, ближайшие недели"
              meta={q.data.moneyHidden ? 'скрыто' : weeks.length ? tengeShort(weeksTotal) : undefined}
            >
              {q.data.moneyHidden ? (
                <p className="text-[14px] text-ink-2">
                  Суммы скрыты: уровень доступа к деньгам назначает владелец.
                </p>
              ) : (
                <>
                  <WeeklyBars
                    points={weeks.map((w) => ({ label: `${w.month} ${w.label}`, value: w.value }))}
                  />
                  <p className="mt-2 text-[12px] text-ink-2">по бюджетам-графикам проектов, млн ₸</p>
                </>
              )}
            </Panel>
          </div>
        </>
      )}
      <ProjectWizard open={wizard} onOpenChange={setWizard} />
    </Page>
  );
}
